import { TextField, getTheme } from '@fluentui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSavePeriodReportComments } from '../fetchData/mutations/useSavePeriodReportComments'
import { PeriodReportData } from '../fetchData/queries/PeriodReportData'
import { useCommentAccess } from '../customHooks/useCommentAccess'
import { getStatusBadgeLabel } from '../StatusBadge'
import styles from './ReportCommentField.module.css'

interface ReportCommentFieldProps {
  periodReport: PeriodReportData | null
}

export const ReportCommentField = ({
  periodReport,
}: ReportCommentFieldProps) => {
  const [localComment, setLocalComment] = useState('')
  const [pendingSave, setPendingSave] = useState(false)
  const { allowedToComment, canEditComment } = useCommentAccess({
    periodReport,
  })

  const saveMutation = useSavePeriodReportComments(periodReport?.id || null)
  const saveMutateRef = useRef(saveMutation.mutate)
  useEffect(() => {
    saveMutateRef.current = saveMutation.mutate
  })

  // Update local state when period report changes
  useEffect(() => {
    if (periodReport) {
      setLocalComment(periodReport.comments?.reportComment || '')
    } else {
      setLocalComment('')
    }
  }, [periodReport])

  // Debounced save effect
  useEffect(() => {
    if (!pendingSave || !periodReport) return

    const debounceDelay = 800

    const timer = setTimeout(() => {
      // Only save if there's an actual change and report is ongoing
      if (
        canEditComment &&
        localComment !== periodReport.comments?.reportComment
      ) {
        saveMutateRef.current({
          reportComment: localComment,
          accountComments: periodReport.comments?.accountComments || [],
        })
      }
      setPendingSave(false)
    }, debounceDelay)

    return () => clearTimeout(timer)
  }, [pendingSave, localComment, periodReport, canEditComment])

  const handleChange = useCallback(
    (
      _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ) => {
      if (!canEditComment) return

      setLocalComment(newValue || '')
      setPendingSave(true)
    },
    [canEditComment]
  )

  // Don't render if no period report is selected
  if (!periodReport) {
    return null
  }

  const isReadOnly = !canEditComment
  const theme = getTheme()
  const isReportClosed = periodReport.status === 'Closed'
  const localizedStatusLabel = getStatusBadgeLabel(periodReport.status)
  const closedReportMessage = `Rapporten är ${localizedStatusLabel.toLowerCase()} och kan inte redigeras`
  const disabledMessage = isReportClosed
    ? closedReportMessage
    : 'Du har inte behörighet att redigera rapportkommentar'
  const isSaving = saveMutation.isPending

  const getPlaceholderText = () => {
    if (isReportClosed) {
      return 'Kommentarer kan endast redigeras för pågående rapporter'
    }

    if (!allowedToComment) {
      return 'Du har inte behörighet att redigera rapportkommentar'
    }

    return 'Lägg till en kommentar för hela rapporten...'
  }

  const getDescriptionText = () => {
    if (isSaving) {
      return 'Sparar...'
    }

    if (isReportClosed) {
      return closedReportMessage
    }

    if (!allowedToComment) {
      return 'Du saknar behörighet att redigera rapportkommentar'
    }

    return 'Kommentaren sparas automatiskt'
  }

  return (
    <div className={styles.reportCommentContainer}>
      <div title={isReadOnly ? disabledMessage : undefined}>
        <TextField
          label="Rapportkommentar"
          multiline
          rows={3}
          value={localComment}
          onChange={handleChange}
          disabled={isReadOnly}
          styles={
            isReadOnly
              ? {
                  root: { opacity: 1 },
                  fieldGroup: {
                    backgroundColor: theme.palette.neutralLighter,
                    borderColor: theme.palette.neutralLight,
                  },
                  field: {
                    color: theme.palette.neutralSecondary,
                    WebkitTextFillColor: theme.palette.neutralSecondary,
                  },
                }
              : undefined
          }
          placeholder={getPlaceholderText()}
          description={getDescriptionText()}
        />
      </div>
    </div>
  )
}
